import * as React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Container,
  Link,
  Grid,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { newStudent, createStudentPayload, Student } from "../../models/student";
import { useSnackbar } from "../../components/snackbar";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;
export default function SignUp() {
    const [student, setStudent] = useState<Student>(newStudent());
    const [states, setStates] = useState<any[]>([]);
    const [lgas, setLgas] = useState<any[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const navigate = useNavigate();

    const [marketingOptIn, setMarketingOptIn] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);

    const [loading, setLoading] = useState(false);

    const { showMessage } = useSnackbar();
    
    useEffect(() => {
        fetch("https://vapi.verifyme.ng/v1/countries/1/states")
        .then((response) => response.json())
        .then((data) => {
            if (data.status === "success") {
                setStates(data.data);
            }
        });
    }, []);

    useEffect(() => {
        if (student.state !== 0) {
            fetch(`https://vapi.verifyme.ng/v1/states/${student.state}/lgas`)
                .then((response) => response.json())
                .then((data) => {
                    if (data.status === "success") {
                        setLgas(data.data);
                    }
                });
        }
    }, [student.state]);

    const handleStudentChange = <K extends keyof Student>(field: K, value: Student[K]) => {
        setStudent((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        if (!student.name || !student.email || !student.phone_number || !student.password || !student.password_repeat || student.state === 0 || student.lga === 0) {
            showMessage('fill in all fields', 1);
            return;            
        }

        if (!/^[a-zA-Z\s'-]+$/.test(student.name)) {
            showMessage("Name can only contain letters, spaces, hyphens, or apostrophes", 1);
            return;
        }

        if (student.password.length < 6) {
            showMessage('password is too short', 0);
            return;
        }

        if (student.password !== student.password_repeat) {
            showMessage('passwords do not match', 1);
            return;
        }

        try {
            setLoading(true);
            await axios.post(`${API_URL}/auth/students/sign-up`, 
                createStudentPayload(student)
            );
            navigate(`/verify/sign-up?email=${student.email}`);
        } catch (error: any) {
            showMessage(error.response?.data?.detail || 'an error occurred', 1);
        } finally {
            setLoading(false);
        }
    };

    const fieldStyles = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            borderRadius: 3,
            '&:hover fieldset': {
                borderColor: '#667eea'
            },
            '&.Mui-focused fieldset': {
                borderColor: '#667eea'
            }
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", display: "flex" }}>
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "grey.100",
                    marginBottom: 4,

                }}
            >
                <Box
                    sx={{
                        p: 3,
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "1.5rem",
                            userSelect: "none",
                            fontFamily: "'Inter', 'Roboto', sans-serif",
                            color: "#3B82F6",
                            letterSpacing: "-0.02em"
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                color: "green",
                                fontWeight: 800,
                                fontSize: "1.6rem"
                            }}
                        >
                            e
                        </Box>
                        LMS
                        <Box
                            component="span"
                            sx={{
                                color: "green",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                ml: 1,
                                opacity: 0.7
                            }}
                        >
                            Student
                        </Box>
                    </Typography>
                </Box>

                {/* Form Container */}
                <Container
                    maxWidth="md"
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        padding: 4
                    }}
                >
                    <Box sx={{ maxWidth: 600, mx: "auto", width: "100%" }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                mb: 1,
                                color: "#1a1a1a"
                            }}
                        >
                            Create Account
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 4,
                                color: "text.secondary"
                            }}
                        >
                            Join our learning platform and start your journey.
                        </Typography>

                        <Stack spacing={3}>
                            {/* Name and Email */}
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Full Name"
                                        variant="outlined"
                                        value={student.name}
                                        onChange={(e) => handleStudentChange("name", e.target.value)}
                                        error={!!errors.name}
                                        helperText={errors.name}
                                        sx={fieldStyles}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        variant="outlined"
                                        type="email"
                                        value={student.email}
                                        onChange={(e) => handleStudentChange("email", e.target.value)}
                                        error={!!errors.email}
                                        helperText={errors.email}
                                        sx={fieldStyles}
                                    />
                                </Grid>
                            </Grid>

                            {/* Phone Number */}
                            <TextField
                                fullWidth
                                label="Phone Number"
                                variant="outlined"
                                type="phone"
                                value={student.phone_number}
                                onChange={(e) => handleStudentChange("phone_number", e.target.value)}
                                error={!!errors.phone_number}
                                helperText={errors.phone_number}
                                sx={fieldStyles}
                            />

                            {/* Password Fields */}
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Password"
                                        variant="outlined"
                                        type={showPassword ? "text" : "password"}
                                        value={student.password}
                                        onChange={(e) => handleStudentChange("password", e.target.value)}
                                        error={!!errors.password}
                                        helperText={errors.password}
                                        sx={fieldStyles}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Confirm Password"
                                        variant="outlined"
                                        type={showPasswordRepeat ? "text" : "password"}
                                        value={student.password_repeat}
                                        onChange={(e) => handleStudentChange("password_repeat", e.target.value)}
                                        error={!!errors.password_repeat}
                                        helperText={errors.password_repeat}
                                        sx={fieldStyles}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}
                                                        edge="end"
                                                    >
                                                        {showPasswordRepeat ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Location Fields */}
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        label="Country"
                                        variant="outlined"
                                        value={student.country}
                                        disabled
                                        sx={fieldStyles}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <FormControl fullWidth error={!!errors.student_state}>
                                        <InputLabel id="state-label">State</InputLabel>
                                        <Select
                                            labelId="state-label"
                                            value={student.state || ""}
                                            onChange={(e) => {
                                                const stateId = Number(e.target.value);
                                                handleStudentChange("state", stateId);
                                                handleStudentChange("lga", 0);
                                            }}
                                            sx={{
                                                backgroundColor: 'white',
                                                borderRadius: 3,
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#667eea'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#667eea'
                                                }
                                            }}
                                        >
                                            <MenuItem value={0}><em>Select State</em></MenuItem>
                                            {states.map((state) => (
                                                <MenuItem key={state.id} value={state.id}>
                                                    {state.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.student_state && (
                                            <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                                                {errors.student_state}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <FormControl fullWidth disabled={student.state === 0} error={!!errors.student_lga}>
                                        <InputLabel id="lga-label">LGA</InputLabel>
                                        <Select
                                            labelId="lga-label"
                                            value={student.lga || ""}
                                            onChange={(e) => handleStudentChange("lga", Number(e.target.value))}
                                            sx={{
                                                backgroundColor: 'white',
                                                borderRadius: 3,
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#667eea'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#667eea'
                                                }
                                            }}
                                        >
                                            <MenuItem value={0}><em>Select LGA</em></MenuItem>
                                            {student.state !== 0 &&
                                                lgas.map((lga) => (
                                                    <MenuItem key={lga.id} value={lga.id}>
                                                        {lga.name}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                        {errors.student_lga && (
                                            <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                                                {errors.student_lga}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>
                            </Grid>


                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={marketingOptIn}
                                        onChange={(e) => setMarketingOptIn(e.target.checked)}
                                        sx={{ color: '#3B82F6', '&.Mui-checked': { color: '#3B82F6' } }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" color="text.secondary">
                                        Send me updates, offers, and product news (optional)
                                    </Typography>
                                }
                                sx={{ alignItems: 'center' }}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={handleSubmit}
                                disabled={loading}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 3,
                                    backgroundColor: '#3B82F6',
                                    boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    mt: 2,
                                    '&:hover': {
                                        boxShadow: "0 6px 25px rgba(102, 126, 234, 0.6)",
                                        transform: "translateY(-1px)"
                                    },
                                    transition: "all 0.3s ease"
                                }}
                            >
                                Create Account
                            </Button>

                            <Stack spacing={0.5} alignItems="center">
                                <Typography variant="body2" color="text.secondary">
                                    Already have an account?{' '}
                                    <Link
                                        component="button"
                                        onClick={() => navigate('/sign-in')}
                                        sx={{
                                            color: '#667eea',
                                            fontWeight: 600,
                                            textDecoration: 'none',
                                            '&:hover': { textDecoration: 'underline' }
                                        }}
                                    >
                                        Sign in
                                    </Link>
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                    By creating an account, you agree to our{' '}
                                    <Link href="/terms" sx={{ color: 'text.disabled' }}>Terms of Use</Link>
                                    {' '}and{' '}
                                    <Link href="/privacy" sx={{ color: 'text.disabled' }}>Privacy Policy</Link>
                                </Typography>
                            </Stack>

                        </Stack>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}
